import { analyticsSearch } from "@/features/analytics/searchState";
/**
 * Code-based TanStack Router tree.
 *
 * The root route is the authenticated shell ({@link RootLayout}); every feature
 * page hangs off it. Paths line up with `ROUTES`/`RESOURCES` in `@/config` so
 * the URL, the nav item and the RBAC resource never drift.
 */
import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
  Link,
} from "@tanstack/react-router";
import { RootLayout } from "./RootLayout";
import { OverviewPage } from "@/features/overview/OverviewPage";

/**
 * The landing page (`OverviewPage`) is bundled eagerly so the first authenticated
 * paint is instant. Every other page — and its heavy deps (tremor charts, the
 * xyflow graph, yjs collaboration, the pipeline studio) — is split into its own
 * chunk via `lazyRouteComponent` and fetched on navigation (`defaultPreload:
 * "intent"` warms it on hover/focus). This keeps the initial bundle small.
 */
const lazyPage = (
  importer: () => Promise<Record<string, React.ComponentType>>,
  name: string,
) => lazyRouteComponent(importer, name);

/** A search param as a non-empty string, or `undefined`. Used by `validateSearch`. */
const str = (value: unknown): string | undefined =>
  typeof value === "string" && value ? value : undefined;

const GettingStartedPage = lazyPage(() => import("@/features/overview/GettingStartedPage"), "GettingStartedPage");
const TransactionsPage = lazyPage(() => import("@/features/transactions/TransactionsPage"), "TransactionsPage");
const TransactionDetailPage = lazyPage(() => import("@/features/transactions/TransactionDetailPage"), "TransactionDetailPage");
const AddressesPage = lazyPage(() => import("@/features/addresses/AddressesPage"), "AddressesPage");
const AddressDetailPage = lazyPage(() => import("@/features/addresses/AddressDetailPage"), "AddressDetailPage");
const CatalogPage = lazyPage(() => import("@/features/catalog/CatalogPage"), "CatalogPage");
const ProposalsPage = lazyPage(() => import("@/features/reviews/ProposalsPage"), "ProposalsPage");
const PoolDetailPage = lazyPage(() => import("@/features/pools/PoolDetailPage"), "PoolDetailPage");
const EntityDetailPage = lazyPage(() => import("@/features/catalog/CatalogDetailRoutes"), "EntityDetailPage");
const TagDetailPage = lazyPage(() => import("@/features/catalog/CatalogDetailRoutes"), "TagDetailPage");
const LabelDetailPage = lazyPage(() => import("@/features/catalog/CatalogDetailRoutes"), "LabelDetailPage");
const GroupDetailPage = lazyPage(() => import("@/features/catalog/CatalogDetailRoutes"), "GroupDetailPage");
const GraphsPage = lazyPage(() => import("@/features/graphs/GraphsPage"), "GraphsPage");
const InvestigationsPage = lazyPage(() => import("@/features/investigations/InvestigationsPage"), "InvestigationsPage");
const InvestigationDetailPage = lazyPage(() => import("@/features/investigations/InvestigationDetailPage"), "InvestigationDetailPage");
const AskAiPage = lazyPage(() => import("@/features/ask-ai/AskAiPage"), "AskAiPage");
const StatsPage = lazyPage(() => import("@/features/stats/StatsPage"), "StatsPage");
const AnalyticsPage = lazyPage(() => import("@/features/analytics/AnalyticsPage"), "AnalyticsPage");
const ExplorerPage = lazyPage(() => import("@/features/analytics/ExplorerPage"), "ExplorerPage");
const ProtocolOverridesPage = lazyPage(() => import("@/features/protocols/ProtocolOverridesPage"), "ProtocolOverridesPage");
const DashboardViewerPage = lazyPage(() => import("@/features/analytics/DashboardViewerPage"), "DashboardViewerPage");
const ActivityPage = lazyPage(() => import("@/features/activity/ActivityPage"), "ActivityPage");
const PipelinesPage = lazyPage(() => import("@/features/pipelines/PipelinesPage"), "PipelinesPage");
const PipelineBuilderPage = lazyPage(() => import("@/features/pipelines/PipelineBuilderPage"), "PipelineBuilderPage");
const RunsPage = lazyPage(() => import("@/features/runs/RunsPage"), "RunsPage");
const TracersPage = lazyPage(() => import("@/features/tracers/TracersPage"), "TracersPage");
const GroupingRulesPage = lazyPage(
  () => import("@/features/grouping/GroupingRulesPage"),
  "GroupingRulesPage",
);
const AlertsPage = lazyPage(() => import("@/features/alerts/AlertsPage"), "AlertsPage");
const WatchesPage = lazyPage(() => import("@/features/watches/WatchesPage"), "WatchesPage");
const WatchDetailPage = lazyPage(
  () => import("@/features/watches/WatchDetailPage"),
  "WatchDetailPage",
);
const SituationsPage = lazyPage(() => import("@/features/watches/SituationsPage"), "SituationsPage");
const ActorsPage = lazyPage(() => import("@/features/actors/ActorsPage"), "ActorsPage");
const FlowsPage = lazyPage(() => import("@/features/flows/FlowsPage"), "FlowsPage");
const ContractsPage = lazyPage(() => import("@/features/contracts/ContractsPage"), "ContractsPage");
const ContractDetailPage = lazyPage(() => import("@/features/contracts/ContractDetailPage"), "ContractDetailPage");
const WalletPage = lazyPage(() => import("@/features/wallet/WalletPage"), "WalletPage");
const TeamPage = lazyPage(() => import("@/features/team/TeamPage"), "TeamPage");
const AcceptTeamInvitePage = lazyPage(() => import("@/features/team/AcceptTeamInvitePage"), "AcceptTeamInvitePage");
const SettingsLayout = lazyPage(() => import("@/features/settings/SettingsLayout"), "SettingsLayout");
const SettingsPage = lazyPage(() => import("@/features/settings/SettingsPage"), "SettingsPage");
const AccessPage = lazyPage(() => import("@/features/settings/AccessPage"), "AccessPage");
const WorkspaceSettingsPage = lazyPage(() => import("@/features/settings/WorkspacePage"), "WorkspacePage");
const DefaultsPage = lazyPage(() => import("@/features/settings/DefaultsPage"), "DefaultsPage");
const ApisPage = lazyPage(() => import("@/features/settings/ApisPage"), "ApisPage");
const BillingPage = lazyPage(() => import("@/features/settings/BillingPage"), "BillingPage");
const ExportsPage = lazyPage(() => import("@/features/settings/ExportsPage"), "ExportsPage");
const ProfilePage = lazyPage(() => import("@/features/profile/ProfilePage"), "ProfilePage");

function NotFound(): React.ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center">
      <h1 className="text-2xl font-semibold text-foreground">Page not found</h1>
      <p className="mt-1 text-sm text-muted-foreground">The page you&apos;re looking for doesn&apos;t exist.</p>
      <Link to="/" className="mt-4 text-sm font-medium text-primary hover:underline">
        Back to overview
      </Link>
    </div>
  );
}

const rootRoute = createRootRoute({
  component: RootLayout,
  notFoundComponent: NotFound,
});

// Settings is a small nested SITE: `SettingsLayout` renders the shared header +
// the grouped left nav (`features/settings/settingsNav.ts`), and the child route
// paints into its `<Outlet />`. One page per subject rather than nine cards on
// the index, so each is deep-linkable and preloads on intent — which is what
// lets a scope pill point at `/settings/defaults#scope-default-visibility`.
//
// Every path here must be in `ROUTES` and named by the settings nav or its
// exemption list; `settingsNav.test.ts` reads this file and asserts both ways.
const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings",
  component: SettingsLayout,
});
const settingsRouteTree = settingsRoute.addChildren([
  createRoute({ getParentRoute: () => settingsRoute, path: "/", component: SettingsPage }),
  createRoute({ getParentRoute: () => settingsRoute, path: "/access", component: AccessPage }),
  createRoute({ getParentRoute: () => settingsRoute, path: "/workspace", component: WorkspaceSettingsPage }),
  createRoute({ getParentRoute: () => settingsRoute, path: "/defaults", component: DefaultsPage }),
  createRoute({ getParentRoute: () => settingsRoute, path: "/apis", component: ApisPage }),
  createRoute({ getParentRoute: () => settingsRoute, path: "/billing", component: BillingPage }),
  createRoute({ getParentRoute: () => settingsRoute, path: "/exports", component: ExportsPage }),
]);

const routes = [
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/",
    component: OverviewPage,
    // `?guide=1` forces the Auth0-style onboarding guide on the landing page
    // even for a set-up / previously-dismissed tenant.
    validateSearch: (search: Record<string, unknown>): { guide?: boolean } => ({
      guide:
        search.guide === 1 ||
        search.guide === "1" ||
        search.guide === true ||
        search.guide === "true"
          ? true
          : undefined,
    }),
  }),
  createRoute({ getParentRoute: () => rootRoute, path: "/getting-started", component: GettingStartedPage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/transactions", component: TransactionsPage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/transactions/$id", component: TransactionDetailPage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/addresses", component: AddressesPage }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/addresses/$id",
    component: AddressDetailPage,
    // `?chain=` preselects a chain on the multi-chain detail page (e.g. when a
    // transaction's from/to drilldown knows which chain it came from).
    //
    // `?tab=` opens one lens directly, same convention as the catalog detail
    // routes below. The scanner needs it: a reader clicking a flagged wallet is
    // asking what it did, and landing them on Overview to hunt for the Conduct
    // tab loses the question they arrived with. Unknown values fall through to
    // the page default rather than 404ing (the page still maps the old
    // `behavior` spelling, since those links are already out there).
    validateSearch: (
      search: Record<string, unknown>,
    ): { chain?: string; tab?: string } => ({
      chain: typeof search.chain === "string" && search.chain ? search.chain : undefined,
      tab: typeof search.tab === "string" && search.tab ? search.tab : undefined,
    }),
  }),
  createRoute({ getParentRoute: () => rootRoute, path: "/catalog", component: CatalogPage }),
  // The human review queue every discovery is quarantined into — the other half
  // of the catalog, and the only surface that can approve a row into it.
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/proposals",
    // `?status=` / `?kind=` so another surface can deep-link the lane it means
    // — the address page's "queued for verification" notice points straight at
    // the pending address proposals. Unknown values fall through to the page's
    // own defaults rather than 404ing.
    validateSearch: (search: Record<string, unknown>): { status?: string; kind?: string } => ({
      status: str(search.status),
      kind: str(search.kind),
    }),
    component: ProposalsPage,
  }),
  // `/entities` and `/groups` were the two pages the catalog replaced. Redirected
  // rather than deleted: they are in bookmarks, in links pasted into cases, and
  // in the onboarding checklist. Their DETAIL routes keep their own paths — an
  // entity's page is about that entity, not about the catalog.
  ...["/entities", "/groups"].map((from) =>
    createRoute({
      getParentRoute: () => rootRoute,
      path: from,
      beforeLoad: () => {
        throw redirect({ to: "/catalog", replace: true });
      },
    }),
  ),
  // The four catalog detail paths share one page; `?tab=` keeps the open lens in
  // the URL so "what this tag is attached to" can be pasted into a case note and
  // open on the same reading. Unknown values fall through to the page's default
  // rather than 404ing — a stale bookmark should still land on the item.
  ...(
    [
      ["/entities/$id", EntityDetailPage],
      ["/groups/$id", GroupDetailPage],
      ["/tags/$id", TagDetailPage],
      ["/labels/$id", LabelDetailPage],
    ] as const
  ).map(([path, component]) =>
    createRoute({
      getParentRoute: () => rootRoute,
      path,
      component,
      validateSearch: (search: Record<string, unknown>): { tab?: string } => ({
        tab: typeof search.tab === "string" && search.tab ? search.tab : undefined,
      }),
    }),
  ),
  createRoute({ getParentRoute: () => rootRoute, path: "/graphs", component: GraphsPage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/investigations", component: InvestigationsPage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/investigations/$id", component: InvestigationDetailPage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/ask-ai", component: AskAiPage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/stats", component: StatsPage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/analytics", component: AnalyticsPage }),
  // Static segment declared before the `$dashboardId` param so it wins the match.
  createRoute({ getParentRoute: () => rootRoute, path: "/analytics/explorer", component: ExplorerPage, validateSearch: analyticsSearch }),
  // `?protocol=` names the selected definition. The page held the selection in
  // component state over a catalogue of HUNDREDS of protocols, so the answer to
  // "how does this platform decode Across?" was not a link — it was a set of
  // directions, and a reload dropped it. It is also the target the transaction
  // page's protocol badges need in order to be links at all.
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/analytics/protocols",
    component: ProtocolOverridesPage,
    validateSearch: (search: Record<string, unknown>): { protocol?: string } => ({
      protocol: str(search.protocol),
    }),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/analytics/$dashboardId",
    component: DashboardViewerPage,
    // `?var_<name>=<value>` seeds the board's template variables, so a board
    // narrowed to one token or pool is a URL — pasteable into a case note, and
    // linkable from the page the subject belongs to. Without it an embedded
    // panel's "open the full board" link landed on the unfiltered board, which
    // reads as though the numbers had changed.
    validateSearch: analyticsSearch,
  }),
  createRoute({ getParentRoute: () => rootRoute, path: "/activity", component: ActivityPage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/pipelines", component: PipelinesPage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/pipelines/$id", component: PipelineBuilderPage }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/runs",
    component: RunsPage,
    // Two prefills arrive here, and they are told apart by which bounds are set.
    // `?chain=&tx=` re-decodes one transaction (the transaction detail page's
    // decode-quality escape hatch). `?chain=&from=&to=` — optionally `&address=`
    // to narrow to one contract — opens a block-range backfill, which is how a
    // watch asks for the history it was created too late to have seen.
    validateSearch: (
      search: Record<string, unknown>,
    ): { chain?: string; tx?: string; from?: string; to?: string; address?: string } => {
      return {
        chain: str(search.chain),
        tx: str(search.tx),
        from: str(search.from),
        to: str(search.to),
        address: str(search.address),
      };
    },
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/tracers",
    component: TracersPage,
    validateSearch: (search: Record<string, unknown>): { run?: string } => ({
      run: str(search.run),
    }),
  }),
  // A tracer IS a thing you send someone — "look at what this run found". The
  // page held its selection in component state, so there was nothing to paste
  // into a case, a ticket or a message, and a reload dropped it.
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/tracers/$id",
    component: TracersPage,
    validateSearch: (search: Record<string, unknown>): { run?: string } => ({
      run: str(search.run),
    }),
  }),
  // A sibling of /pipelines rather than a tab on /runs: these are
  // configuration, Runs is the operational record of what ran.
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/grouping-rules",
    component: GroupingRulesPage,
  }),
  createRoute({ getParentRoute: () => rootRoute, path: "/alerts", component: AlertsPage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/watches", component: WatchesPage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/watches/$id", component: WatchDetailPage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/situations", component: SituationsPage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/actors", component: ActorsPage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/flows", component: FlowsPage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/contracts", component: ContractsPage }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/contracts/$chainId/$address",
    component: ContractDetailPage,
    // `?block=` pins every read on the page to a block height. In the URL rather
    // than component state because a contract's ABI and its proxy implementation
    // are both block-ranged: "this contract at block N" is the shareable thing an
    // operator sends to a colleague, and without the pin in the link the colleague
    // opens the same page resolved at the chain head.
    //
    // `?tab=` names which of the explorer's thirteen tabs opens, and `?tx=` the
    // transaction this contract was opened FROM. Together they are the return
    // leg of the "make this transaction decode" round trip: a reader sent here
    // by a decode gap used to land on Fields, hunt for "Edit" along a wrapped
    // tab strip, and then have no way back to the transaction and no re-decode
    // action — so the workflow had a forward link and nothing else.
    //
    // `tx` is the bare 0x hash, deliberately: the chain is already in the route
    // (a transaction and the contract it called are on the same chain), and the
    // page composes both the `{slug}:{hash}` ref `/transactions/$id` wants and
    // the `?chain=&tx=` pair `/runs` validates. Shape-checked here so a
    // malformed value drops out instead of rendering a dead link.
    validateSearch: (
      search: Record<string, unknown>,
    ): { block?: string; tab?: string; tx?: string } => ({
      block:
        typeof search.block === "string" && /^\d+$/.test(search.block) ? search.block : undefined,
      tab: str(search.tab),
      tx:
        typeof search.tx === "string" && /^0x[0-9a-fA-F]{64}$/.test(search.tx)
          ? search.tx
          : undefined,
    }),
  }),
  // Chain-scoped like a contract, and for the same reason: a pool exists on ONE
  // chain. `$address` is the pool's own contract (V2/V3) or a V4 pool id — hence
  // no address-shaped validation on it.
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/pools/$chainId/$address",
    component: PoolDetailPage,
  }),
  createRoute({ getParentRoute: () => rootRoute, path: "/wallet", component: WalletPage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/team", component: TeamPage }),
  // Team-invite acceptance landing (from the branded email). `?token=` is the
  // secret accept-token; the page POSTs it to `/teams/invites/accept`.
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/teams/accept-invite",
    component: AcceptTeamInvitePage,
    validateSearch: (search: Record<string, unknown>): { token?: string } => ({
      token: typeof search.token === "string" && search.token ? search.token : undefined,
    }),
  }),
  settingsRouteTree,
  createRoute({ getParentRoute: () => rootRoute, path: "/profile", component: ProfilePage }),
];

const routeTree = rootRoute.addChildren(routes);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  // Keep `:` literal in path params so a chain-prefixed transaction id renders
  // as `/transactions/eth:0x…` (the form `transactions-mgmt-api` expects) rather
  // than the percent-encoded `eth%3A0x…`.
  pathParamsAllowedCharacters: [":"],
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
